import React, {ReactElement, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {toast} from 'react-toastify';
import {uuid} from '../../../../utils/utils';
import EventSequence from '../../../../@types/event-sequence';
import {TestMappingProgress, TestMappingResultMessage} from '../../../../@types/test-mapping';
import {MappingDendrogramView} from '../../tabeditor/editors/irp-workbook/components/ReportMappingDendrogram';
import HighlightedNarrative from '../../../common/HighlightedNarrative.component';
import {getModelTestingCache, TestMappingCacheEntry} from './modelTestingCache';
import {
    TestingScope,
    modelForScope,
    scopeKey,
    startTestMappingForScope,
    testMappingSnapshotForScope,
} from './testing-scope';

type Phase = 'input' | 'running' | 'result' | 'error';

const POLL_INTERVAL_MS = 1500;
const MAX_CONSECUTIVE_POLL_ERRORS = 3;

/**
 * Single-report test mapping panel, hosted as the "Test Report" tab of the
 * Model Testing modal. Owns its own run lifecycle; polling stops on unmount
 * (the modal unmounts its panels when closed).
 */
export default function TestReportMappingPanel(
    props: { scope: TestingScope | null }): ReactElement {
    const {scope} = props;
    const cacheKey = scope ? scopeKey(scope) : null;
    const [phase, setPhase] = useState<Phase>('input');
    const [reportId, setReportId] = useState<string>('');
    const [reportContent, setReportContent] = useState<string>('');
    const [esd, setEsd] = useState<EventSequence | null>(null);
    const [log, setLog] = useState<TestMappingProgress[]>([]);
    const [result, setResult] = useState<TestMappingResultMessage | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [resultTab, setResultTab] = useState<number>(0); // 0 = Report, 1 = Log
    const [highlightedExcerpts, setHighlightedExcerpts] = useState<string[]>([]);

    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeRunIdRef = useRef<string | null>(null);

    const stopPolling = (): void => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        activeRunIdRef.current = null;
    };

    // Stop any in-flight polling when the modal unmounts.
    useEffect((): (() => void) => stopPolling, []);

    // Restore the most recent test run for this model (finished results reappear,
    // an in-flight run resumes polling); "Test another report" clears it.
    useEffect((): void => {
        if (scope == null || cacheKey == null) {
            return;
        }
        const cached = getModelTestingCache(cacheKey).testMapping;
        if (!cached) {
            return;
        }
        setReportId(cached.reportId);
        setEsd(cached.esd);
        setLog(cached.log);
        setResult(cached.result);
        setErrorMessage(cached.errorMessage);
        if (cached.errorMessage != null) {
            setPhase('error');
        } else if (cached.result != null) {
            setPhase('result');
        } else {
            setPhase('running');
            activeRunIdRef.current = cached.runId;
            void poll(scope, cached.runId, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey]);

    const updateCache = (key: string, patch: Partial<TestMappingCacheEntry>): void => {
        const cache = getModelTestingCache(key);
        if (cache.testMapping) {
            Object.assign(cache.testMapping, patch);
        }
    };

    const poll = async (target: TestingScope, theRunId: string, consecutiveErrors: number): Promise<void> => {
        const key = scopeKey(target);
        if (activeRunIdRef.current !== theRunId) {
            return;
        }
        try {
            const snapshot = await testMappingSnapshotForScope(target, theRunId);
            if (activeRunIdRef.current !== theRunId) {
                return;
            }
            setLog(snapshot.log);
            updateCache(key, {log: snapshot.log});
            if (snapshot.error) {
                setErrorMessage(snapshot.error.message);
                updateCache(key, {errorMessage: snapshot.error.message});
                setPhase('error');
                stopPolling();
                return;
            }
            if (snapshot.result) {
                setResult(snapshot.result);
                updateCache(key, {result: snapshot.result});
                setPhase('result');
                stopPolling();
                return;
            }
            pollTimerRef.current = setTimeout(() => { void poll(target, theRunId, 0); }, POLL_INTERVAL_MS);
        } catch (error) {
            if (activeRunIdRef.current !== theRunId) {
                return;
            }
            if (consecutiveErrors + 1 >= MAX_CONSECUTIVE_POLL_ERRORS) {
                setErrorMessage((error as Error).message);
                setPhase('error');
                stopPolling();
                return;
            }
            pollTimerRef.current = setTimeout(() => { void poll(target, theRunId, consecutiveErrors + 1); }, POLL_INTERVAL_MS);
        }
    };

    const handleRun = async (): Promise<void> => {
        if (scope == null || cacheKey == null) {
            return;
        }
        const newRunId = uuid();
        stopPolling();
        setLog([]);
        setResult(null);
        setErrorMessage(null);
        activeRunIdRef.current = newRunId;
        setPhase('running');
        try {
            const fullEsd: EventSequence = await modelForScope(scope);
            setEsd(fullEsd);
            getModelTestingCache(cacheKey).testMapping = {
                runId: newRunId, reportId: reportId.trim(), esd: fullEsd,
                log: [], result: null, errorMessage: null,
            };
            const request = reportId.trim()
                ? {runId: newRunId, reportId: reportId.trim()}
                : {runId: newRunId, reportContent};
            await startTestMappingForScope(scope, request);
            void poll(scope, newRunId, 0);
        } catch (error) {
            toast.error((error as Error).message);
            setErrorMessage((error as Error).message);
            updateCache(cacheKey, {errorMessage: (error as Error).message});
            setPhase('error');
            stopPolling();
        }
    };

    const resetToInput = (): void => {
        stopPolling();
        if (cacheKey != null) {
            delete getModelTestingCache(cacheKey).testMapping;
        }
        setReportId('');
        setReportContent('');
        setEsd(null);
        setLog([]);
        setResult(null);
        setErrorMessage(null);
        setHighlightedExcerpts([]);
        setResultTab(0);
        setPhase('input');
    };

    const canRun = scope != null && (reportId.trim() !== '' || reportContent.trim() !== '');

    const renderLogLines = (): ReactElement[] => log.map((line) => (
        <div key={line.seq} style={{marginBottom: 3}}>
            <div>{`▸ ${line.detail}`}</div>
            {line.justification && (
                <div style={{paddingLeft: 14, opacity: 0.65, whiteSpace: 'pre-wrap'}}>{line.justification}</div>
            )}
        </div>
    ));

    return (
        <Box>
            {phase === 'input' && (
                <Stack spacing={2} sx={{mt: 1}}>
                    <TextField
                        label="Report ID"
                        size="small"
                        value={reportId}
                        onChange={(e): void => setReportId(e.target.value)}
                        disabled={reportContent.trim() !== ''}
                        helperText="The report's identifier, e.g. DFW08LA089"
                    />
                    <Typography variant="caption" align="center">or</Typography>
                    <TextField
                        label="Paste report text"
                        size="small"
                        multiline
                        minRows={4}
                        value={reportContent}
                        onChange={(e): void => setReportContent(e.target.value)}
                        disabled={reportId.trim() !== ''}
                    />
                </Stack>
            )}
            {phase === 'running' && (
                <Box sx={{fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto'}}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 1}}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Mapping… ({log.length} steps)</Typography>
                    </Stack>
                    {renderLogLines()}
                </Box>
            )}
            {phase === 'result' && esd && (
                <Box sx={{display: 'flex', gap: 1, height: 480}}>
                    {/* minWidth: 0 on both columns: flex items default to min-width auto, so wide
                        report content would otherwise inflate the side panel and squeeze the dendrogram */}
                    <Box sx={{flex: 1.4, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0}}>
                        <MappingDendrogramView
                            selectedReportId={0}
                            nodeMappings={result?.mappings ?? []}
                            eventSequenceData={esd}
                            onNodeHover={setHighlightedExcerpts}
                        />
                    </Box>
                    <Box sx={{flex: 0.8, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, borderLeft: '1px solid #ddd', pl: 1}}>
                        <Tabs value={resultTab} onChange={(_e, v): void => setResultTab(v)} variant="fullWidth">
                            <Tab label="Report" />
                            <Tab label="Log" />
                        </Tabs>
                        <Box sx={{flex: 1, overflow: 'auto', mt: 1, overflowWrap: 'anywhere'}}>
                            {resultTab === 0 ? (
                                <HighlightedNarrative
                                    narrative={result?.reportText ?? ''}
                                    highlightPhrases={highlightedExcerpts}
                                />
                            ) : (
                                <Box sx={{fontFamily: 'monospace', fontSize: 11}}>
                                    <Typography variant="caption" fontWeight={600}>
                                        {result?.status} · {result?.mappedNodeCount} nodes · conf {result?.avgConfidence?.toFixed(2)}
                                    </Typography>
                                    {result?.rejectionReason && (
                                        <Typography variant="body2" sx={{mt: 1}}>{result.rejectionReason}</Typography>
                                    )}
                                    {renderLogLines()}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}
            {phase === 'error' && (
                <Typography color="error" sx={{mt: 1}}>
                    {errorMessage ?? 'The test mapping failed. Please try again.'}
                </Typography>
            )}
            <Stack direction="row" justifyContent="flex-end" sx={{mt: 2}}>
                {phase === 'input' && (
                    <Button variant="contained" onClick={(): void => { void handleRun(); }} disabled={!canRun}>
                        Run mapping
                    </Button>
                )}
                {(phase === 'result' || phase === 'error') && (
                    <Button variant="contained" onClick={resetToInput}>Test another report</Button>
                )}
            </Stack>
        </Box>
    );
}
